import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    AfterViewInit,
    OnDestroy,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    catchError,
    finalize,
    map,
    Observable,
    of,
    Subject,
    takeUntil,
    tap,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { Country } from '../../../models/domain/Country';
import { CountryService } from '../../../services/country.service';
import { LoadingService } from '../../../services/loading.service';
import { ObjectUtility } from '../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../validators/ValidationService';
import { State } from '../../../models/domain/State';
import { City } from '../../../models/domain/City';
import { CreateB2BClient } from '../../../models/domain/B2BClient/CreateB2BClient';
import { AuthenticationService } from '../../../services/authentication.service';
import { StateService } from '../../../services/state.service';
import { CityService } from '../../../services/city.service';
import { UrlHelper } from '../../../utilities/UrlHelper';

@Component({
    selector: 'app-b2bclient-details',
    templateUrl: './b2bclient-details.component.html',
    styleUrl: './b2bclient-details.component.scss',
    imports: [ReactiveFormsModule, CommonModule],
})
export class B2BClientDetailsComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @Output() classApplied = new EventEmitter<boolean>();
    @Output() saveConfirmationEvent = new EventEmitter<boolean>();
    @Input() data!: CreateB2BClient;
    // isToggled
    isToggled = false;
    frmGroup!: FormGroup;

    private destroy$: Subject<boolean> = new Subject<boolean>();
    public b2bcientOriginal = new CreateB2BClient();
    public b2bcient: CreateB2BClient = new CreateB2BClient();
    public titleText = '';
    public titleButton = '';
    public errorMessage = '';
    public currentUserId = 0;
    isSubmitted = false;

    //dropdown DataBind
    public country$!: Observable<Country[]>;

    public country!: Country[];
    public state!: State[];
    public city!: City[];

    public pageIndex = 0;
    public pageSize = 1000;
    public searchOptions!: BaseSearchOptions;

    constructor(
        public themeService: CustomizerSettingsService,
        private formBuilder: FormBuilder,
        private toasterService: ToastrService,
        private loadingService: LoadingService,
        private countryService: CountryService,
        private stateService: StateService,
        private cityService: CityService,
        private authenticationService: AuthenticationService,
        private cdr: ChangeDetectorRef,
    ) {
        this.initialize();
    }

    public ngOnInit(): void {
        this.getDropdownData();

        if (this.data) {
            this.currentUserId = this.data.userId ? this.data.userId : 0;

            this.b2bcient = ObjectUtility.deepClone(this.data);
            this.b2bcientOriginal = ObjectUtility.deepClone(this.data);
            this.setForm(this.b2bcient);
            this.initDropdownsForEdit();
        } else {
            this.currentUserId = 0;
            const newB2bcient = new CreateB2BClient();
            this.b2bcient = ObjectUtility.deepClone(newB2bcient);
            this.b2bcientOriginal = ObjectUtility.deepClone(newB2bcient);
            this.setForm(this.b2bcient);
        }
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });

        this.searchOptions = new BaseSearchOptions(
            undefined,
            'name',
            0,
            this.pageIndex,
            this.pageSize,
        );
        this.isSubmitted = false;
        this.createForm();
    }

    public ngAfterViewInit(): void {
        //B2BClient
        this.frmGroup.controls['firstNameControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.firstName = value;
            },
        );

        this.frmGroup.controls['lastNameControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.lastName = value;
            },
        );

        this.frmGroup.controls['emailControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.email = value;
            },
        );

        this.frmGroup.controls['phoneControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.phone = value;
            },
        );

        this.frmGroup.controls['passwordControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.password = value;
            },
        );

        //Tenant
        this.frmGroup.controls['tenantNameControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.tenantName = value;
            },
        );

        this.frmGroup.controls['descriptionControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.description = value;
            },
        );

        this.frmGroup.controls['logoUrlControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.logoUrl = value;
            },
        );

        this.frmGroup.controls['isMainTenantControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.isMainTenant = value;
            },
        );

        //Client
        this.frmGroup.controls['clientNameControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.clientName = value;
            },
        );

        //Address
        this.frmGroup.controls['addressLine1Control'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.address.addressLine1 = value;
            },
        );
        this.frmGroup.controls['addressLine2Control'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.address.addressLine2 = value;
            },
        );
        this.frmGroup.controls['landmarkControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.address.landmark = value;
            },
        );
        this.frmGroup.controls['pinCodeControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.address.pinCode = value;
            },
        );
        this.frmGroup.controls['countryNameControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.address.country = value;
            },
        );
        this.frmGroup.controls['stateNameControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.address.state = value;
            },
        );
        this.frmGroup.controls['cityNameControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.address.city = value;
            },
        );

        this.frmGroup.controls['isActiveControl'].valueChanges.subscribe(
            (value) => {
                this.b2bcient.isActive = value;
            },
        );
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    get f() {
        return this.frmGroup.controls;
    }

    private createForm(): void {
        this.frmGroup = this.formBuilder.group({
            firstNameControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            lastNameControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            emailControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            phoneControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            passwordControl: new FormControl('', [whitespaceValidator()]),
            tenantNameControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            descriptionControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            logoUrlControl: new FormControl(''),
            isMainTenantControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            clientNameControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            addressLine1Control: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            addressLine2Control: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            landmarkControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            pinCodeControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            countryNameControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            stateNameControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            cityNameControl: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            isActiveControl: new FormControl(true),
        });
    }

    private setForm(createb2b: CreateB2BClient): void {
        if (createb2b) {
            this.frmGroup.patchValue({
                firstNameControl: createb2b.firstName,
                lastNameControl: createb2b.lastName,
                emailControl: createb2b.email,
                phoneControl: createb2b.phone,
                passwordControl: createb2b.password,
                tenantNameControl: createb2b.tenantName,
                descriptionControl: createb2b.description,
                logoUrlControl: createb2b.logoUrl,
                isMainTenantControl: createb2b.isMainTenant,
                clientNameControl: createb2b.clientName,
                addressLine1Control: createb2b.address.addressLine1,
                addressLine2Control: createb2b.address.addressLine2,
                landmarkControl: createb2b.address.landmark,
                pinCodeControl: createb2b.address.pinCode,
                countryNameControl: createb2b.address.country,
                stateNameControl: createb2b.address.state,
                cityNameControl: createb2b.address.city,
                isActiveControl: createb2b.isActive ?? true,
            });
        }
        this.titleText = `${
            this.currentUserId === 0 ? 'Add' : 'Edit'
        } B2B Client`;
        this.titleButton = `${this.currentUserId === 0 ? 'Create' : 'Update'}`;
    }

    public onSave(): void {
        this.isSubmitted = true;
        if (this.frmGroup.valid) {
            this.loadingService.showLoading();
            this.addEditTenant();
        }
    }

    private addEditTenant(): void {
        if (this.b2bcient?.id === 0) {
            this.authenticationService
                .createB2BClient(this.sanitizeService(this.b2bcient))
                .pipe(
                    catchError((err: any) => {
                        if (err && err.error) {
                            this.isSubmitted = false;
                            if (err.error.isCustom) {
                                this.errorMessage = err.error.message;
                                this.toasterService.warning(err.error.message);
                            } else {
                                this.toasterService.error(
                                    'Something went wrong!',
                                );
                            }
                        } else {
                            this.isSubmitted = false;
                            this.toasterService.error('Something went wrong!');
                        }
                        this.loadingService.hideLoading();

                        return of(null);
                    }),
                    finalize(() => {
                        this.loadingService.hideLoading();
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe((response) => {
                    if (response) {
                        this.isSubmitted = false;
                        this.b2bcient =
                            response?.body != null
                                ? response?.body
                                : new CreateB2BClient();
                        this.saveConfirmationEvent.emit(true);
                        this.toggleClass();
                        this.toasterService.success('Save successfully');
                    }
                    this.loadingService.hideLoading();
                });
        } else {
            this.authenticationService
                .updateB2BClient(this.sanitizeService(this.b2bcient))
                .pipe(
                    catchError((err: any) => {
                        if (err && err.error) {
                            this.isSubmitted = false;
                            if (err.error.isCustom) {
                                this.errorMessage = err.error.message;
                                this.toasterService.warning(err.error.message);
                            } else {
                                this.toasterService.error(
                                    'Something went wrong!',
                                );
                            }
                        } else {
                            this.isSubmitted = false;
                            this.toasterService.error('Something went wrong!');
                        }
                        return of(null);
                    }),
                    takeUntil(this.destroy$),
                    // Ensure loading is always hidden, even if error is not caught
                    finalize(() => {
                        this.loadingService.hideLoading();
                    }),
                )
                .subscribe((response) => {
                    if (response) {
                        this.isSubmitted = false;
                        this.b2bcient =
                            response?.body != null
                                ? response?.body
                                : new CreateB2BClient();
                        this.saveConfirmationEvent.emit(true);
                        this.toasterService.success('Update successfully');
                        this.toggleClass();
                    }
                });
        }
    }

    public toggleClass(): void {
        this.classApplied.emit(false);
    }

    public onLogoFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const reader = new FileReader();
            reader.onload = () => {
                this.b2bcient.logoBase64 = reader.result as string;
            };
            reader.readAsDataURL(input.files[0]);
        }
    }

    public dataHasChanged(): boolean {
        const baseEqual = ObjectUtility.deepEqual(
            this.b2bcient,
            this.b2bcientOriginal,
        );
        return !baseEqual;
    }

    public formIsValid(): boolean {
        const isValid =
            this.frmGroup.valid && this.dataHasChanged() ? true : false;
        return isValid;
    }

    public countryNameComparer(t1: Country, t2: Country): boolean {
        return t1 && t2 ? t1.id === t2.id || t1.name === t2.name : t1 === t2;
    }

    public stateNameComparer(t1: State, t2: State): boolean {
        return t1 && t2 ? t1.id === t2.id || t1.name === t2.name : t1 === t2;
    }

    public cityNameComparer(t1: City, t2: City): boolean {
        return t1 && t2 ? t1.id === t2.id || t1.name === t2.name : t1 === t2;
    }

    private sanitizeService(b2bClient: CreateB2BClient): CreateB2BClient {
        b2bClient.address.business = null;

        b2bClient.address.country = null;
        b2bClient.address.countryId =
            this.frmGroup.controls['countryNameControl'].value.id;

        b2bClient.address.state = null;
        b2bClient.address.stateId =
            this.frmGroup.controls['stateNameControl'].value.id;

        b2bClient.address.city = null;
        b2bClient.address.cityId =
            this.frmGroup.controls['cityNameControl'].value.id;

        b2bClient.password = 'Admin@123';
        return b2bClient;
    }

    private getDropdownData() {
        this.loadingService.showLoading();

        this.country$ = this.countryService.get(this.searchOptions).pipe(
            map((result: any) => {
                this.loadingService.hideLoading();
                return result.body.values;
            }),
            catchError(() => {
                this.loadingService.hideLoading();
                return of([]);
            }),
        );
    }

    public onCountryChange(value: Country) {
        if (value) {
            this.getStateByCountry(value.id);
        }
    }

    public onStateChange(value: State) {
        if (value) {
            this.getCityByState(value.id);
        }
    }

    private initDropdownsForEdit(): void {
        const countryId = this.b2bcient?.address?.countryId || this.b2bcient?.address?.country?.id;
        const stateId = this.b2bcient?.address?.stateId || this.b2bcient?.address?.state?.id;
        const cityId = this.b2bcient?.address?.cityId || this.b2bcient?.address?.city?.id;

        if (!countryId) return;

        this.loadingService.showLoading();
        this.stateService.getStateByCountry(countryId)
            .pipe(
                takeUntil(this.destroy$),
                tap((response: any) => {
                    this.state = response.body ?? [];
                    const selectedState = this.state.find(s => s.id === stateId) || null;
                    this.frmGroup.patchValue({ stateNameControl: selectedState });
                    this.b2bcient.address.state = selectedState;
                    this.cdr.markForCheck();
                }),
                catchError(() => {
                    this.state = [];
                    return of(null);
                }),
                finalize(() => this.loadingService.hideLoading())
            )
            .subscribe(() => {
                if (!stateId) return;
                this.loadingService.showLoading();
                this.cityService.getCityByState(stateId)
                    .pipe(
                        takeUntil(this.destroy$),
                        tap((response: any) => {
                            this.city = response.body ?? [];
                            const selectedCity = this.city.find(c => c.id === cityId) || null;
                            this.frmGroup.patchValue({ cityNameControl: selectedCity });
                            this.b2bcient.address.city = selectedCity;
                            this.cdr.markForCheck();
                        }),
                        catchError(() => {
                            this.city = [];
                            return of(null);
                        }),
                        finalize(() => this.loadingService.hideLoading())
                    )
                    .subscribe();
            });
    }

    public getStateByCountry(countryId: number | undefined): void {
        if (countryId && countryId > 0) {
            this.loadingService.showLoading();

            this.stateService.getStateByCountry(countryId)
                .pipe(
                    takeUntil(this.destroy$),
                    tap((response: any) => {
                        this.state = response.body ?? [];
                        this.frmGroup.patchValue({ stateNameControl: null });
                        this.b2bcient.address.state = null;
                        this.city = [];
                        this.frmGroup.patchValue({ cityNameControl: null });
                        this.b2bcient.address.city = null;
                    }),
                    catchError(() => {
                        this.toasterService.error('Failed to load states.');
                        this.state = [];
                        this.frmGroup.patchValue({ stateNameControl: null });
                        return of(null);
                    }),
                    finalize(() => this.loadingService.hideLoading())
                )
                .subscribe();
        } else {
            this.state = [];
            this.frmGroup.patchValue({ stateNameControl: null });
        }
    }

    public getCityByState(stateId: number | undefined): void {
        if (stateId && stateId > 0) {
            this.loadingService.showLoading();

            this.cityService.getCityByState(stateId)
                .pipe(
                    takeUntil(this.destroy$),
                    tap((response: any) => {
                        this.city = response.body ?? [];
                        this.frmGroup.patchValue({ cityNameControl: null });
                        this.b2bcient.address.city = null;
                    }),
                    catchError(() => {
                        this.toasterService.error('Failed to load cities.');
                        this.city = [];
                        this.frmGroup.patchValue({ cityNameControl: null });
                        return of(null);
                    }),
                    finalize(() => this.loadingService.hideLoading())
                )
                .subscribe();
        } else {
            this.city = [];
            this.frmGroup.patchValue({ cityNameControl: null });
        }
    }

    public getLogoPreviewUrl(): string {
        if (this.b2bcient.logoBase64) return this.b2bcient.logoBase64;
        if (!this.b2bcient.logoUrl) return '';
        return UrlHelper.getImageUrl(this.b2bcient.logoUrl);
    }
}
